import {Component, OnInit, OnDestroy, TemplateRef, ChangeDetectionStrategy, HostListener} from '@angular/core';
import {
  ElkExceptionService,
  ElkSearchMetadata,
  JsonExceptionHolder
} from './elk-exception.service';
import {Pair} from '../code-review/code-review-models';
import {ToastrService} from 'ngx-toastr';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Subscription} from 'rxjs';

const FAILED_TO_LOAD_DATA = 'failed to load data';

@Component({
    selector: 'app-elk-exception',
    templateUrl: './elk-exception.component.html',
    styleUrls: ['./elk-exception.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true
})
export class ElkExceptionComponent implements OnInit, OnDestroy {

  exceptions: Pair<ElkSearchMetadata, JsonExceptionHolder>[][] = [];
  totalExceptionCount = 0;
  modalExceptionText = '';
  hoveredIndex: number | undefined;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private elkExceptionService: ElkExceptionService,
    private notificationService: ToastrService,
    private modalService: NgbModal,
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadData() {
    this.fetchMore();
  }

  private fetchMore(dateGreaterThan?: string) {
    this.subscriptions.add(
      this.elkExceptionService.getExceptions(dateGreaterThan).subscribe({
        next: res => {
          if (this.subscriptions.closed || res.length === 0) {
            return;
          }

          this.exceptions = [...this.exceptions, ...res];
          this.updateTotalExceptions();

          const latestDate = this.getLatestTimestampAcrossAll(res);
          if (latestDate) {
            this.fetchMore(latestDate);
          }
        },
        error: err => {
          this.notificationService.error(`${FAILED_TO_LOAD_DATA} ${JSON.stringify(err)}`);
          console.error(FAILED_TO_LOAD_DATA, err);
        }
      }));
  }

  private getLatestTimestampAcrossAll(exceptionGrps: Pair<ElkSearchMetadata, JsonExceptionHolder>[][]): string | undefined {
    let latest: string | undefined;
    exceptionGrps.forEach(grp => {
      grp.forEach(it => {
        const timestamp = it.second['@timestamp'];
        if (!latest || timestamp > latest) {
          latest = timestamp;
        }
      });
    });
    return latest;
  }

  private updateTotalExceptions() {
    this.totalExceptionCount = this.exceptions.reduce((num, x) => num + x.length, 0);
  }

  getWhereOfGrp(exceptionGrp: Pair<ElkSearchMetadata, JsonExceptionHolder>[]): string {
    const rawValues = exceptionGrp.map(it => `${it.second.hostname} - ${it.second.application}`);
    const set = new Set(rawValues);
    return Array.from(set).reduce((s1, s2) => `${s1}, ${s2}`);
  }

  getOldest(exceptionGrp: Pair<ElkSearchMetadata, JsonExceptionHolder>[]) {
    const sorted = exceptionGrp.map(it => it.second['@timestamp']).sort((a, b) => Date.parse(a) - Date.parse(b));
    return sorted[0].replace('T', ' ');
  }

  getNewest(exceptionGrp: Pair<ElkSearchMetadata, JsonExceptionHolder>[]) {
    const sorted = exceptionGrp.map(it => it.second['@timestamp']).sort((a, b) => Date.parse(a) - Date.parse(b));
    return sorted[sorted.length - 1].replace('T', ' ');
  }

  showFull(content: TemplateRef<unknown>, exceptionGrp: Pair<ElkSearchMetadata, JsonExceptionHolder>[]) {
    this.modalExceptionText = exceptionGrp[0].second.stacktrace;
    this.modalService.open(content, {size: 'xl'});
  }

  accept(exceptionGrp: Pair<ElkSearchMetadata, JsonExceptionHolder>[], index: number) {
    this.exceptions.splice(index, 1);
    this.updateTotalExceptions();
    this.elkExceptionService.addToBlacklist(exceptionGrp.map(it => ({
      id: undefined as number | undefined,
      index: it.first.index,
      elkId: it.first.id,
      date: undefined as string | undefined,
    }))).subscribe({
      next: _ => {
        console.info('silenced exceptions', exceptionGrp);
      },
      error: err => {
        this.notificationService.error(`${FAILED_TO_LOAD_DATA} ${JSON.stringify(err)}`);
        console.error(FAILED_TO_LOAD_DATA, err);
      }
    });
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'x' || event.key === 'X') {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || this.modalService.hasOpenModals()) {
        return;
      }
      if (this.exceptions.length > 0) {
        const index = this.hoveredIndex !== undefined && this.hoveredIndex < this.exceptions.length ? this.hoveredIndex : 0;
        this.accept(this.exceptions[index], index);
      }
    }
  }
}
